<template>
  <div class="pb-[7px] flex flex-col">
    <div
      data-intercom-target="Positive sentiment checkbox"
      class="sentiment-row"
    >
      <div class="flex flex-row items-center font-body-2 gap-[10px]">
        <OnclusiveCheckbox
          :checked="positiveCheckboxStatus"
          @checked="(value) => checkboxClicked(value, 'positive')"
        />

        <div class="flex items-center gap-[4px]">
          <div class="w-[16px] h-[16px] flex items-center justify-center">
            <font-awesome-icon
              class="icon text-success-500"
              :icon="['far', 'smile']"
              style="width: 13.33px; height: 13.33px"
            />
          </div>
          {{ $t("appliedFilters.positive") }}
        </div>
      </div>
    </div>
    <div
      data-intercom-target="Neutral sentiment checkbox"
      class="sentiment-row"
    >
      <div class="flex flex-row items-center font-body-2 gap-[10px]">
        <OnclusiveCheckbox
          :checked="neutralCheckboxStatus"
          @checked="(value) => checkboxClicked(value, 'neutral')"
        />
        <div class="flex items-center gap-[4px]">
          <div class="w-[16px] h-[16px] flex items-center justify-center">
            <font-awesome-icon
              :icon="['far', 'meh']"
              class="text-slate-500"
              style="width: 13.33px; height: 13.33px"
            />
          </div>
          {{ $t("appliedFilters.neutral") }}
        </div>
      </div>
    </div>
    <div
      data-intercom-target="Negative sentiment checkbox"
      class="sentiment-row"
    >
      <div class="flex flex-row items-center font-body-2 gap-[10px]">
        <OnclusiveCheckbox
          :checked="negativeCheckboxStatus"
          @checked="(value) => checkboxClicked(value, 'negative')"
        />
        <div class="flex items-center gap-[4px]">
          <div class="w-[16px] h-[16px] flex items-center justify-center">
            <font-awesome-icon
              class="icon text-danger-600"
              :icon="['far', 'frown']"
              style="width: 13.33px; height: 13.33px"
            />
          </div>
          {{ $t("appliedFilters.negative") }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Ref, ref, onMounted, watch } from "vue";
import OnclusiveCheckbox from "~/components/shared/onclusive-checkbox/OnclusiveCheckbox.vue";
import { useSearchFiltersStore } from "~/store/searchFilter";

const searchFilterStore = useSearchFiltersStore();
const currentSearchFilter = searchFilterStore.getCurrentSearchFilter;
const emit = defineEmits(["performUpdate"]);

const checkboxClicked = (value: boolean, property: string) => {
  switch (property) {
    case "positive":
      positiveCheckboxStatus.value = value;
      break;
    case "neutral":
      neutralCheckboxStatus.value = value;
      break;
    case "negative":
      negativeCheckboxStatus.value = value;
      break;
    default:
      break;
  }

  const conditions = [
    positiveCheckboxStatus.value && "positive",
    neutralCheckboxStatus.value && "neutral",
    negativeCheckboxStatus.value && "negative",
  ];

  const conditionsString = conditions.filter(Boolean).join("|");

  searchFilterStore.updateArticleSentiment(conditionsString);
  emit("performUpdate");
};

const positiveCheckboxStatus: Ref<boolean> = ref(false);
const neutralCheckboxStatus: Ref<boolean> = ref(false);
const negativeCheckboxStatus: Ref<boolean> = ref(false);

onMounted(() => {
  if (currentSearchFilter.filter_article_sentiment?.includes("positive")) {
    positiveCheckboxStatus.value = true;
  }

  if (currentSearchFilter.filter_article_sentiment?.includes("neutral")) {
    neutralCheckboxStatus.value = true;
  }

  if (currentSearchFilter.filter_article_sentiment?.includes("negative")) {
    negativeCheckboxStatus.value = true;
  }
});

// Watch for changes in getCurrentSearchFilter
watch(
  () => searchFilterStore.getCurrentSearchFilter,
  (newValue) => {
    // Update refs based on the new value
    positiveCheckboxStatus.value =
      newValue.filter_article_sentiment.includes("positive");
    neutralCheckboxStatus.value =
      newValue.filter_article_sentiment.includes("neutral");
    negativeCheckboxStatus.value =
      newValue.filter_article_sentiment.includes("negative");
  },
);
</script>

<style scoped>
.icon {
  @apply w-4 h-4 shrink-0;
}
.sentiment-row {
  @apply flex flex-row gap-x-2 items-center text-gray-700 text-sm font-medium font-['Inter'] leading-none py-[7px];
}
</style>
